'use client';

import { PdfViewer } from '@bw/elements';
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ThemeColor } from '../../theme';
import { Button } from '../utility/button/button';
import styles from './PdfViewerButton.module.scss';

type Props = {
	title: string;
	href: string;
	textColor: ThemeColor;
	backgroundColor: ThemeColor;
	borderColor: ThemeColor;
	type?: string;
	openInNewTab?: boolean;
};

export function PdfViewerButton({ title, href, textColor, backgroundColor, borderColor, type, openInNewTab }: Props) {
	const [open, setOpen] = useState(false);

	const onClick = useCallback(() => setOpen(true), []);
	const close = useCallback(() => setOpen(false), []);

	useEffect(() => {
		if (!open) return;
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') close();
		};
		document.addEventListener('keydown', onKey);
		const prevOverflow = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
		return () => {
			document.removeEventListener('keydown', onKey);
			document.body.style.overflow = prevOverflow;
		};
	}, [open, close]);

	const modal = open && (
		<div
			className={styles.overlay}
			onClick={close}
			role="dialog"
			aria-modal="true"
			aria-label={title}
		>
			<div className={styles.modal} onClick={(e) => e.stopPropagation()}>
				<button
					className={styles.closeBtn}
					onClick={close}
					aria-label="Close"
					title="Close"
				>
					×
				</button>
				<PdfViewer url={href} title={''} fallbackHref={href} />
			</div>
		</div>
	);

	return (
		<>
			<Button
				title={title}
				textColor={textColor}
				backgroundColor={backgroundColor}
				borderColor={borderColor}
				type={type}
				openInNewTab={openInNewTab}
				onClick={onClick}
			/>
			{modal && typeof document !== 'undefined'
				? createPortal(modal, document.body)
				: null}
		</>
	);
}
